// src/components/dashboard/NetWorthCard.jsx
import { useState, useMemo } from "react";
import { AreaChart, Area, ResponsiveContainer, Tooltip } from "recharts";
import { cn, formatCurrency, formatDate } from "../../lib/utils";
import { Card } from "../ui/primitives";
import { useNetWorthHistory } from "../../hooks/useFinancialData";

const RANGES = ["1M", "3M", "6M", "1Y", "All"];

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-background-elevated border border-border rounded-xl px-3 py-2 shadow-card">
      <p className="text-[10px] text-text-tertiary mb-0.5">{formatDate(d.date?.toDate?.() || d.date, "MMM d, yyyy")}</p>
      <p className="text-sm font-semibold font-tabular text-text-primary">
        {formatCurrency(d.value)}
      </p>
    </div>
  );
};

export function NetWorthCard({ totalAssets, totalLiabilities, breakEvenDate }) {
  const [range, setRange] = useState("1M");
  const { history, loading } = useNetWorthHistory(range);

  const netWorth = totalAssets - totalLiabilities;

  const { change, changeLabel, startLabel, endLabel, chartData } = useMemo(() => {
    if (!history.length) return { change: 0, changeLabel: "", startLabel: "", endLabel: "", chartData: [] };

    const first = history[0];
    const last = history[history.length - 1];
    const firstVal = first?.value || 0;
    const lastVal = last?.value || netWorth;
    const diff = lastVal - firstVal;

    const startDate = first?.date?.toDate?.() || new Date();
    const endDate = new Date();

    return {
      change: diff,
      changeLabel: `${diff >= 0 ? "+" : ""}${formatCurrency(diff)} · ${range}`,
      startLabel: formatDate(startDate, "MMM d"),
      endLabel: formatDate(endDate, "MMM d"),
      chartData: history.map((h) => ({
        date: h.date,
        value: h.value,
      })),
    };
  }, [history, range, netWorth]);

  // Fallback chart data when Firestore is empty (sandbox/demo)
  const displayData = chartData.length > 0 ? chartData : generateDemoData(range, netWorth);

  return (
    <Card className="p-5">
      <p className="label mb-2">Net worth</p>
      <div className="flex items-end gap-3 mb-1">
        <span className="font-display text-3xl font-bold text-text-primary font-tabular">
          {formatCurrency(netWorth)}
        </span>
      </div>
      <span className={cn("text-xs font-medium", change >= 0 ? "text-accent-green" : "text-accent-red")}>
        {changeLabel}
      </span>

      {/* Range toggle */}
      <div className="flex gap-1.5 mt-3 mb-3">
        {RANGES.map((r) => (
          <button
            key={r}
            onClick={() => setRange(r)}
            className={cn(
              "px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all duration-150",
              range === r
                ? "bg-accent-green-muted text-accent-green"
                : "text-text-tertiary hover:text-text-secondary"
            )}
          >
            {r}
          </button>
        ))}
      </div>

      {/* Chart */}
      <div className="h-20 w-full -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={displayData} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
            <defs>
              <linearGradient id="nwGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00E5A0" stopOpacity={0.2} />
                <stop offset="100%" stopColor="#00E5A0" stopOpacity={0} />
              </linearGradient>
            </defs>
            <Tooltip content={<CustomTooltip />} cursor={false} />
            <Area
              type="monotone"
              dataKey="value"
              stroke="#00E5A0"
              strokeWidth={1.5}
              fill="url(#nwGrad)"
              dot={false}
              activeDot={{ r: 3, fill: "#00E5A0", stroke: "#0A0A0F", strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Date labels */}
      <div className="flex justify-between mt-1">
        <span className="text-[10px] text-text-tertiary font-mono">{startLabel}</span>
        <span className="text-[10px] text-text-tertiary font-mono">{endLabel}</span>
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-border">
        <div className="text-center">
          <p className="label mb-1">Assets</p>
          <p className="text-sm font-semibold text-accent-green font-tabular">
            {formatCurrency(totalAssets, { compact: true })}
          </p>
        </div>
        <div className="text-center border-x border-border">
          <p className="label mb-1">Liabilities</p>
          <p className="text-sm font-semibold text-accent-red font-tabular">
            -{formatCurrency(totalLiabilities, { compact: true })}
          </p>
        </div>
        <div className="text-center">
          <p className="label mb-1">Break-even</p>
          <p className="text-sm font-semibold text-text-secondary">
            {breakEvenDate ? formatDate(breakEvenDate, "MMM yyyy") : "—"}
          </p>
        </div>
      </div>
    </Card>
  );
}

function generateDemoData(range, currentValue) {
  const points = { "1M": 30, "3M": 90, "6M": 180, "1Y": 365, All: 730 }[range] || 30;
  const data = [];
  const step = points / 10;
  for (let i = 0; i <= 10; i++) {
    const daysAgo = points - i * step;
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    const noise = (Math.random() - 0.3) * (currentValue * 0.03);
    const trend = (i / 10) * (currentValue * 0.03);
    data.push({ date, value: currentValue - currentValue * 0.03 + trend + noise });
  }
  return data;
}
